// train-trip-stops.js — derives per-train stop sequences for card expansion.
//
// Canonical contract (tests/commuter-features.test.ts, absorbed from main):
// suburban sequences are walked along the hand-curated corridor
// (ROUTE_STATIONS_SEQ) using GTFS-published TRAIN_DEPARTURES to align real
// times station-by-station; the Milano Passante segment (Certosa → Forlanini)
// is not served by every run in the feed, so those offsets are appended from
// the fixed timetable constants shared with main.
//
// Phase 8 of feature/figma-alignment.

import { TRAIN_STATIONS, TRAIN_DEPARTURES } from "../data/trains.js";

const ROUTE_STATIONS_SEQ = {
  to_milano: {
    S5: ["BS_FS", "LG_FS", "CN_FS", "PB_FS", "VZ_FS", "RH_FS"],
    S6: ["PG_FS", "RH_FS"],
    RE_4: ["BS_FS", "RH_FS"],
    RE_5: ["BS_FS", "LG_FS", "RH_FS"],
    R21: ["LG_FS", "CN_FS", "PB_FS", "RH_FS"],
    R23: ["BS_FS", "LG_FS", "PB_FS", "RH_FS"]
  },
  from_milano: {
    S5: ["RH_FS", "VZ_FS", "PB_FS", "CN_FS", "LG_FS", "BS_FS"],
    S6: ["RH_FS", "PG_FS"],
    RE_4: ["RH_FS", "BS_FS"],
    RE_5: ["RH_FS", "LG_FS", "BS_FS"],
    R21: ["RH_FS", "PB_FS", "CN_FS", "LG_FS"],
    R23: ["RH_FS", "PB_FS", "LG_FS", "BS_FS"]
  }
};

/**
 * Derive the stop sequence for a single train departure.
 *
 * @param {string} routeId     e.g. "RE_4", "S5", "R21"
 * @param {string} direction   "to_milano" | "from_milano"
 * @param {string} dayType     "weekday" | "saturday" | "sunday"
 * @param {string} departStationCode   e.g. "BS_FS" (our code)
 * @param {number} departMin   minutes-from-midnight of the departure at that station
 * @returns {Array<{code?: string, name: string, min: number, isTrainStation?: boolean}>}
 *          chronological; from_milano starts from the Milano-side terminal
 */
export function deriveTrainTripStops(routeId, direction, dayType, departStationCode, departMin) {
  const startMin = departMin;
  const activeStation = departStationCode;
  const seq = ROUTE_STATIONS_SEQ[direction]?.[routeId] || [];
  const stops = [];

  if (seq.length === 0) return [];

  if (direction === "to_milano") {
    const idx = seq.indexOf(activeStation);
    if (idx === -1) return [];

    let currentMin = startMin;
    stops.push({
      code: activeStation,
      name: TRAIN_STATIONS[activeStation]?.name || activeStation,
      min: currentMin,
      isTrainStation: true
    });

    for (let i = idx + 1; i < seq.length; i++) {
      const nextStation = seq[i];
      const nextDeps = TRAIN_DEPARTURES[nextStation]?.[routeId]?.[`${dayType}_to_milano`] || [];
      const matchingMin = nextDeps.find((m) => m > currentMin && m <= currentMin + 10);
      if (matchingMin !== undefined) {
        currentMin = matchingMin;
      } else {
        currentMin += 5;
      }
      stops.push({
        code: nextStation,
        name: TRAIN_STATIONS[nextStation]?.name || nextStation,
        min: currentMin,
        isTrainStation: true
      });
    }

    const rhoMin = currentMin;
    if (routeId === "S5" || routeId === "S6") {
      stops.push({ name: "Milano Certosa", min: rhoMin + 6 });
      stops.push({ name: "Milano Villapizzone", min: rhoMin + 9 });
      stops.push({ name: "Milano Lancetti", min: rhoMin + 13 });
      stops.push({ name: "Milano Porta Garibaldi Passante", min: rhoMin + 15 });
      stops.push({ name: "Milano Repubblica", min: rhoMin + 17 });
      stops.push({ name: "Milano Porta Venezia", min: rhoMin + 19 });
      stops.push({ name: "Milano Dateo", min: rhoMin + 21 });
      stops.push({ name: "Milano Porta Vittoria", min: rhoMin + 23 });
      stops.push({ name: "Milano Forlanini", min: rhoMin + 26 });
    } else if (routeId === "RE_4" || routeId === "R23") {
      stops.push({ name: "Milano Porta Garibaldi Superficie", min: rhoMin + 14 });
      stops.push({ name: "Milano Centrale", min: rhoMin + 20 });
    } else {
      stops.push({ name: "Milano Porta Garibaldi Superficie", min: rhoMin + 15 });
    }
  } else {
    const idx = seq.indexOf(activeStation);
    if (idx === -1) return [];

    const targetSeq = seq.slice(0, idx + 1);
    const times = new Array(targetSeq.length);
    times[idx] = startMin;

    for (let i = idx - 1; i >= 0; i--) {
      const currentStation = targetSeq[i];
      const nextTime = times[i + 1];
      const currentDeps =
        TRAIN_DEPARTURES[currentStation]?.[routeId]?.[`${dayType}_from_milano`] || [];
      const matchingMin = [...currentDeps]
        .reverse()
        .find((m) => m < nextTime && m >= nextTime - 10);
      if (matchingMin !== undefined) {
        times[i] = matchingMin;
      } else {
        times[i] = nextTime - 5;
      }
    }

    const rhoMin = times[0];

    if (routeId === "S5" || routeId === "S6") {
      stops.unshift({ name: "Milano Certosa", min: rhoMin - 6 });
      stops.unshift({ name: "Milano Villapizzone", min: rhoMin - 9 });
      stops.unshift({ name: "Milano Lancetti", min: rhoMin - 13 });
      stops.unshift({ name: "Milano Porta Garibaldi Passante", min: rhoMin - 15 });
      stops.unshift({ name: "Milano Repubblica", min: rhoMin - 17 });
      stops.unshift({ name: "Milano Porta Venezia", min: rhoMin - 19 });
    } else if (routeId === "RE_4" || routeId === "R23") {
      stops.unshift({ name: "Milano Porta Garibaldi Superficie", min: rhoMin - 14 });
      stops.unshift({ name: "Milano Centrale", min: rhoMin - 20 });
    } else {
      stops.unshift({ name: "Milano Porta Garibaldi Superficie", min: rhoMin - 15 });
    }

    for (let i = 0; i < targetSeq.length; i++) {
      stops.push({
        code: targetSeq[i],
        name: TRAIN_STATIONS[targetSeq[i]]?.name || targetSeq[i],
        min: times[i],
        isTrainStation: true
      });
    }
  }

  return stops;
}
